import { TopicTreeNode } from '../../../types/TopicCustomization';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { cloneDeep, isEqual } from 'lodash';
import Tooltip from '@mui/material/Tooltip';
import OraSwitch from '../../CustomMUI/Switch';
import TriangleRight from '../../../assets/icons/triangle-right.svg';
import TriangleDown from '../../../assets/icons/triangle-down.svg';
import { hapticsImpactLight } from '../../../utils/haptics';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Search } from 'react-feather';
import Checkbox from '../../Input/Checkbox';

interface TopicTreeProps {
  onTopicChange: (
    topics: TopicTreeNode[],
    hasChanges: boolean,
    allActive: boolean,
    allInactive: boolean
  ) => void;
  useSwitches?: boolean;
}

const TopicTree: React.FC<TopicTreeProps> = ({
  onTopicChange,
  useSwitches = true,
}) => {
  const topicTreeFromRedux =
    useSelector((state: RootState) => state.activeTopics.topicTree) || [];

  const [tempTopicFilters, setTempTopicFilters] = useState<TopicTreeNode[]>([]);
  const [originalTopicFilters, setOriginalTopicFilters] = useState<
    TopicTreeNode[]
  >([]);
  const [allTopicsActiveLocal, setAllTopicsActiveLocal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [manualExpandedTopics, setManualExpandedTopics] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    const localCopy = cloneDeep(topicTreeFromRedux);
    setTempTopicFilters(localCopy);
    setOriginalTopicFilters(localCopy);
    setAllTopicsActiveLocal(isAllActive(localCopy));
  }, [topicTreeFromRedux]);

  const handleTopicChange = (topicId: string, active: boolean) => {
    const updateTopicFilters = (
      topics: TopicTreeNode[],
      parentActive: boolean = true
    ): TopicTreeNode[] => {
      return topics.map((topic) => {
        const updatedTopic = { ...topic };
        if (updatedTopic.id === topicId) {
          updatedTopic.active = active;
          if (active && !parentActive) {
            parentActive = true;
          }
          if (
            active &&
            updatedTopic.children &&
            updatedTopic.children.every((child) => !child.active)
          ) {
            updatedTopic.children = updatedTopic.children.map((child) => ({
              ...child,
              active: true,
            }));
          }
        }

        if (updatedTopic.children) {
          updatedTopic.children = updateTopicFilters(
            updatedTopic.children,
            updatedTopic.active && parentActive
          );
        }

        if (!parentActive) {
          updatedTopic.active = false;
          if (updatedTopic.children) {
            updatedTopic.children = updatedTopic.children.map((child) => ({
              ...child,
              active: false,
            }));
          }
        }

        return updatedTopic;
      });
    };

    const activateParent = (
      topics: TopicTreeNode[],
      myTopicId: string
    ): TopicTreeNode[] => {
      return topics.map((topic) => {
        const updatedTopic = { ...topic };
        if (updatedTopic.children) {
          updatedTopic.children = activateParent(
            updatedTopic.children,
            myTopicId
          );
          if (
            updatedTopic.children.some(
              (child) => child.id === myTopicId && child.active
            )
          ) {
            updatedTopic.active = true;
          }
        }
        return updatedTopic;
      });
    };

    let updatedTopics = updateTopicFilters(cloneDeep(tempTopicFilters));
    if (active) {
      updatedTopics = activateParent(updatedTopics, topicId);
    }

    const allActive = isAllActive(updatedTopics);
    const allInactive = isAllInactive(updatedTopics);

    setAllTopicsActiveLocal(allActive);
    setTempTopicFilters(updatedTopics);

    onTopicChange(
      updatedTopics,
      !isEqual(updatedTopics, originalTopicFilters),
      allActive,
      allInactive
    );
  };

  const isTopicMatching = (topic: TopicTreeNode): boolean => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const matchesName = topic.name.toLowerCase().includes(query);
    const hasMatchingChildren =
      topic.children?.some((child) =>
        child.name.toLowerCase().includes(query)
      ) ?? false;
    return matchesName || hasMatchingChildren;
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setExpandedTopics(new Set());
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const newExpandedTopics = new Set<string>();

    const findMatchingTopics = (topics: TopicTreeNode[]) => {
      topics.forEach((topic) => {
        const hasMatchingChildren = topic.children?.some((child) =>
          child.name.toLowerCase().includes(query)
        );

        if (hasMatchingChildren) {
          newExpandedTopics.add(topic.id);
          if (topic.children) {
            findMatchingTopics(topic.children);
          }
        }
      });
    };

    findMatchingTopics(tempTopicFilters);
    setExpandedTopics(newExpandedTopics);
  }, [searchQuery, tempTopicFilters]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const renderTopicFilters = (topics: TopicTreeNode[]) => {
    if (!topics.length) return null;

    return topics.map((t, i) => (
      <Accordion
        key={i}
        className="topic-accordion"
        expanded={
          searchQuery.trim()
            ? expandedTopics.has(t.id)
            : manualExpandedTopics.has(t.id)
        }
        onChange={(_, isExpanded) => {
          if (!searchQuery.trim()) {
            setManualExpandedTopics((prev) => {
              const newSet = new Set(prev);
              if (isExpanded) {
                newSet.add(t.id);
              } else {
                newSet.delete(t.id);
              }
              return newSet;
            });
          }
        }}
        sx={{
          display: isTopicMatching(t) ? 'block' : 'none',
        }}
      >
        <AccordionSummary
          sx={
            i !== topics.length - 1
              ? { borderBottom: '1px solid var(--border-glass)', padding: 0 }
              : { padding: 0 }
          }
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            hapticsImpactLight();
          }}
        >
          {t.children && t.children.length > 0 && (
            <>
              <img
                className="expand-icon expand-icon--closed"
                src={TriangleRight}
                alt=""
              />
              <img
                className="expand-icon expand-icon--open"
                src={TriangleDown}
                alt=""
              />
            </>
          )}
          <div className="row">
            <div>{t.name}</div>
            {useSwitches ? (
              <OraSwitch
                checked={t.active}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleTopicChange(t.id, e.target.checked)}
              />
            ) : (
              <Checkbox
                checked={t.active}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleTopicChange(t.id, e.target.checked)}
              />
            )}
          </div>
        </AccordionSummary>
        {t.children && t.children.length > 0 && (
          <AccordionDetails>
            <div className="level-2">{renderLevel2Topics(t.children)}</div>
          </AccordionDetails>
        )}
      </Accordion>
    ));
  };

  const renderLevel2Topics = (topics: TopicTreeNode[]) => {
    return topics.map((t, i) => (
      <div
        key={'level-2-' + i}
        className="row"
        style={{ display: isTopicMatching(t) ? 'flex' : 'none' }}
      >
        <div>{t.name}</div>
        {useSwitches ? (
          <OraSwitch
            checked={t.active}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleTopicChange(t.id, e.target.checked)}
          />
        ) : (
          <Checkbox
            checked={t.active}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleTopicChange(t.id, e.target.checked)}
          />
        )}
      </div>
    ));
  };

  const handleEnableAllFilters = () => {
    const updatedFilters = bulkChangeTopicFilters(
      cloneDeep(tempTopicFilters),
      true
    );
    setTempTopicFilters(updatedFilters);
    setAllTopicsActiveLocal(true);
    onTopicChange(
      updatedFilters,
      !isEqual(updatedFilters, originalTopicFilters),
      true,
      false
    );
  };

  const handleDisableAllFilters = () => {
    const updatedFilters = bulkChangeTopicFilters(
      cloneDeep(tempTopicFilters),
      false
    );
    setTempTopicFilters(updatedFilters);
    setAllTopicsActiveLocal(false);
    onTopicChange(
      updatedFilters,
      !isEqual(updatedFilters, originalTopicFilters),
      false,
      true
    );
  };

  const bulkChangeTopicFilters = (
    topicFilters: TopicTreeNode[],
    enable: boolean
  ): TopicTreeNode[] => {
    return topicFilters.map((topicFilter) =>
      changeTopicFilter({ ...topicFilter }, enable)
    );
  };

  const changeTopicFilter = (
    topicFilter: TopicTreeNode,
    enable: boolean
  ): TopicTreeNode => {
    topicFilter.active = enable;
    if (topicFilter.children) {
      topicFilter.children = topicFilter.children.map((child) =>
        changeTopicFilter({ ...child }, enable)
      );
    }
    return topicFilter;
  };

  const isAllActive = (topics: TopicTreeNode[]): boolean => {
    return topics.every((t) => {
      if (!t.active) return false;
      return t.children ? isAllActive(t.children) : true;
    });
  };

  const isAllInactive = (topics: TopicTreeNode[]): boolean => {
    return topics.every((t) => {
      if (t.active) return false;
      return t.children ? isAllInactive(t.children) : true;
    });
  };

  return (
    <div className="topic-tree">
      <div className="topic-tree_header">
        <div className="topic-tree_header_left">
          <div className="topic-tree_header_left_label">All Topics</div>
          <div className="topic-tree_header_left_search">
            <Search />
            <input
              placeholder="Find a topic..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="topic-tree_header_toggle">
          <Tooltip title="Toggle all topics" enterTouchDelay={0}>
            <div className="toggle-all-wrapper">
              {useSwitches ? (
                <OraSwitch
                  checked={allTopicsActiveLocal}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleEnableAllFilters();
                    } else {
                      handleDisableAllFilters();
                    }
                  }}
                />
              ) : (
                <Checkbox
                  checked={allTopicsActiveLocal}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleEnableAllFilters();
                    } else {
                      handleDisableAllFilters();
                    }
                  }}
                />
              )}
            </div>
          </Tooltip>
        </div>
      </div>
      <div>{renderTopicFilters(tempTopicFilters)}</div>
    </div>
  );
};

export default TopicTree;
